import { useMemo } from 'react';
import Fuse from 'fuse.js';
import { Product } from '../types';

// ── Normalize ──────────────────────────────────────────────
const normalize = (s: string) =>
  s.replace(/[®™©]+/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

// ── Numbers ────────────────────────────────────────────────
const extractNumbers = (s: string): string[] => s.match(/\d+/g) || [];

const numbersMatch = (queryNums: string[], text: string): boolean => {
  if (queryNums.length === 0) return true;
  const textNums = extractNumbers(text);
  return queryNums.every(n => textNums.includes(n));
};

// ── Keyboard layout ru→en / en→ru ──────────────────────────
const RU = 'йцукенгшщзхъфывапролджэячсмитьбю.еЙЦУКЕНГШЩЗХЪФЫВАПРОЛДЖЭЯЧСМИТЬБЮ,Е';
const EN = "qwertyuiop[]asdfghjkl;'zxcvbnm,./`QWERTYUIOP{}ASDFGHJKL:\"ZXCVBNM<>?~";

const ruToEnMap = new Map<string, string>();
const enToRuMap = new Map<string, string>();
for (let i = 0; i < RU.length; i++) {
  ruToEnMap.set(RU[i], EN[i]);
  enToRuMap.set(EN[i], RU[i]);
}

const convertLayout = (s: string, map: Map<string, string>): string =>
  Array.from(s).map(ch => map.get(ch) ?? ch).join('');

const isRussian = (s: string) => /[а-яёА-ЯЁ]/.test(s);

// ── Synonyms / abbreviations ───────────────────────────────
const SYNONYMS: Record<string, string[]> = {
  'гта': ['grand theft auto', 'gta'],
  'gta': ['grand theft auto'],
  'кс': ['counter-strike', 'counter strike', 'cs'],
  'cs': ['counter-strike', 'counter strike'],
  'фифа': ['fifa', 'ea sports fc', 'ea fc'],
  'fifa': ['ea sports fc', 'ea fc'],
  'фк': ['fc', 'ea sports fc'],
  'fc': ['ea sports fc', 'ea fc'],
  'рдр': ['red dead redemption', 'rdr'],
  'rdr': ['red dead redemption'],
  'нба': ['nba'],
  'нхл': ['nhl'],
  'мк': ['mortal kombat', 'mk'],
  'mk': ['mortal kombat'],
  'кд': ['cyberpunk', 'cd'],
  'тлоу': ['the last of us', 'tlou'],
  'tlou': ['the last of us'],
  'гов': ['god of war', 'gow'],
  'gow': ['god of war'],
  'хог': ['hogwarts legacy'],
  'ведьмак': ['witcher'],
  'ассасин': ['assassin', "assassin's creed"],
  'спайдермен': ['spider-man', 'spider man'],
  'спайдерман': ['spider-man', 'spider man'],
  'человек паук': ['spider-man', 'spider man'],
  'батлфилд': ['battlefield'],
  'резидент': ['resident evil'],
  'горизонт': ['horizon'],
  'анчартед': ['uncharted'],
  'хитман': ['hitman'],
  'дьябло': ['diablo'],
  'элден': ['elden ring'],
  'старфилд': ['starfield'],
  'киберпанк': ['cyberpunk'],
};

/** Expand query into alternative search strings via synonyms */
const expandSynonyms = (q: string): string[] => {
  const alts: string[] = [];
  // Check full query
  if (SYNONYMS[q]) alts.push(...SYNONYMS[q]);
  // Check individual words for partial synonym matches
  const words = q.split(' ');
  for (const word of words) {
    if (SYNONYMS[word]) {
      for (const syn of SYNONYMS[word]) {
        alts.push(q.replace(word, syn));
      }
    }
  }
  return alts;
};

// ── Main hook ──────────────────────────────────────────────
export function useProductSearch(products: Product[], query: string): Product[] {
  const productsLen = products.length;

  const fuse = useMemo(() => {
    const f = new Fuse(products, {
      keys: [
        { name: 'normalizedName', weight: 2 },
        { name: 'subtitle', weight: 1 },
      ],
      getFn: (obj: Product, path: string | string[]) => {
        const key = Array.isArray(path) ? path[0] : path;
        if (key === 'normalizedName') return normalize(obj.name);
        if (key === 'subtitle') return obj.subtitle ? normalize(obj.subtitle) : '';
        return '';
      },
      threshold: 0.3,
      distance: 100,
      minMatchCharLength: 2,
      includeScore: true,
      ignoreLocation: true,
    });
    return f;
  }, [productsLen]); // eslint-disable-line react-hooks/exhaustive-deps

  return useMemo(() => {
    const trimmed = normalize(query);
    if (!trimmed || trimmed.length < 2) return [];

    // 1. Build all query variants: original + layout converted + synonyms
    const queries = new Set<string>([trimmed]);

    // Layout conversion
    if (isRussian(trimmed)) {
      queries.add(normalize(convertLayout(trimmed, ruToEnMap)));
    } else {
      const converted = normalize(convertLayout(trimmed, enToRuMap));
      if (isRussian(converted)) queries.add(converted);
    }

    // Synonyms for each variant so far
    for (const q of Array.from(queries)) {
      for (const syn of expandSynonyms(q)) {
        queries.add(normalize(syn));
      }
    }

    // 2. Run Fuse.js for each variant, deduplicate by product id
    const seen = new Set<string>();
    type FuseResult = { item: Product; score?: number };
    const allResults: FuseResult[] = [];

    for (const q of Array.from(queries)) {
      if (q.length < 2) continue;
      const results = fuse.search(q, { limit: 80 });
      for (const r of results) {
        if (!seen.has(r.item.id)) {
          seen.add(r.item.id);
          allResults.push(r);
        }
      }
    }

    // 3. Enforce exact number matching
    const queryNums = extractNumbers(trimmed);
    const afterNumFilter = queryNums.length > 0
      ? allResults.filter(r => {
          const name = normalize(r.item.name);
          const subtitle = r.item.subtitle ? normalize(r.item.subtitle) : '';
          return numbersMatch(queryNums, name) || numbersMatch(queryNums, subtitle);
        })
      : allResults;

    // 4. Tokenized AND-filter: every non-numeric word in query must appear in name/subtitle
    const queryWords = trimmed.replace(/\d+/g, '').split(/\s+/).filter(w => w.length >= 2);
    const afterTokenFilter = queryWords.length > 1
      ? afterNumFilter.filter(r => {
          const name = normalize(r.item.name);
          const subtitle = r.item.subtitle ? normalize(r.item.subtitle) : '';
          const text = name + ' ' + subtitle;
          return queryWords.every(w => text.includes(w));
        })
      : afterNumFilter;

    // If token filter is too strict and yields 0, fall back to number-only filter
    const filtered = afterTokenFilter.length > 0 ? afterTokenFilter : afterNumFilter;

    // 5. Sort: exact substring first, then by category (games before DLC)
    const categoryOrder = (cat: string) => {
      if (cat === 'game') return 0;
      if (cat === 'subscription') return 1;
      if (cat === 'topup') return 2;
      return 3; // donat last
    };

    const sorted = [...filtered].sort((a, b) => {
      const nameA = normalize(a.item.name);
      const nameB = normalize(b.item.name);
      const exactA = nameA.includes(trimmed) ? 0 : 1;
      const exactB = nameB.includes(trimmed) ? 0 : 1;
      // 1. Exact substring matches first
      if (exactA !== exactB) return exactA - exactB;
      // 2. Games before DLC/donat
      const catA = categoryOrder(a.item.category);
      const catB = categoryOrder(b.item.category);
      if (catA !== catB) return catA - catB;
      // 3. By Fuse score
      return (a.score ?? 1) - (b.score ?? 1);
    });

    return sorted.slice(0, 50).map(r => r.item);
  }, [fuse, query]);
}
